import { contactPerk } from '../../contacts/perks';
import type { GameContext } from '../../core/context';
import type { V2 } from '../../core/math';
import type { System } from '../../core/system';
import { touchUi } from '../../input/touch';
import type { OnlineGame } from '../../online-ui/game';
import type { StaticCollider } from '../../physics/body';
import { Layer } from '../../physics/types';
import { sceneOverride } from '../../render/sceneOverride';
import { WEAPONS, type WeaponId } from '../weaponData';
import { BackRoomScene } from './backRoomScene';
import { CounterUi, type CounterState } from './counterUi';
import { HUT, buildHut, hutPlacement } from './hut';
import { weaponCard } from './shopStats';
import { SID_LINES, SID_NAME } from './sid';
import { ONLINE_DEALER, cardButtons, offlineLines, onlineLines, priceText, type CounterLine } from './stock';
import { BackRoomVisit, type VisitPhase } from './visit';

/**
 * Sid's back room (order 715; docs/WEAPONS.md §6): the fence's shop is a hut in Gorringe's yard you
 * walk into. E at the door fades to black and puts up a small interior INSTEAD of the city
 * (render/sceneOverride.ts), staged as a showcase: the weapon on show hangs large over the counter as
 * a real model, turning under a key light; the strip / arrows / 1–6 / a swipe browse, and the panel
 * shows its numbers from the roster (shopStats.ts), the price and BUY. ✕ / Esc / B (pad B) fades back
 * out at the door.
 *
 * Offline the purchase is the weapons service's `buy` (the fence's rules, unchanged); online it is the
 * existing server-validated `supplierBuy` at the arms dealer (no new money path, no protocol change).
 * The room is built on the way in and disposed on the way out; the city isn't drawn while it is up.
 */

export interface BackRoomService {
  /** At the door or inside (anything but the street). */
  readonly open: boolean;
  readonly phase: VisitPhase;
}

export interface BackRoomInfo {
  phase: VisitPhase;
  open: boolean;
  /** The room is on screen (and the city isn't). */
  roomUp: boolean;
  items: WeaponId[];
  /** The weapon in the showcase. */
  selected: WeaponId | null;
  card: { name: string; price: string; lethal: boolean; bars: { key: string; value: number; text: string }[] } | null;
  buy: { label: string; enabled: boolean } | null;
  /** £ short for the weapon on show (0: you can pay or have it). */
  short: number;
  ammo: { label: string; enabled: boolean } | null;
  why: string;
  /** Sid's line under the blurb (flavour). */
  say: string;
}

declare module '../../core/services' {
  interface ServiceMap {
    backRoom: BackRoomService;
  }
}

declare module '../../core/hooks' {
  interface BhHooks {
    /** Sid's back room: where the visit is, what's on the counter, the price card in focus (JSON). */
    backRoomState(): BackRoomInfo;
    /** Where a weapon's chip in the back room's strip is on screen (CSS px), for a real tap; null if it isn't there. */
    backRoomScreenPos(weapon: string): { x: number; y: number } | null;
  }
}

/** How close to the door spot E reaches (m). */
const DOOR_REACH = 2.6;

export function createBackRoomSystem(): System {
  let ctx: GameContext;
  const visit = new BackRoomVisit();
  let door: { pos: V2; heading: number } | null = null;
  let collider: StaticCollider | null = null;
  let hut: ReturnType<typeof buildHut> | null = null;
  let ui: CounterUi | null = null;
  let room: BackRoomScene | null = null;
  let lines: CounterLine[] = [];
  let index = -1;
  let quote = '';
  let note = '';
  let noteBad = false;
  let busy = false;
  let said = 0;
  let clock = 0;
  let viewKey = '';
  /** The card is redrawn on every change, and twice a second for what changes on its own (cash online). */
  let nextRefresh = 0;
  const offs: (() => void)[] = [];

  const online = (): boolean => ctx.services.tryGet('economy')?.authority === 'server';
  const game = (): OnlineGame | undefined => ctx.services.tryGet('online');
  const weapons = () => ctx.services.tryGet('weapons');
  const pickLine = <T>(list: readonly T[]): T => list[said++ % list.length]!;

  function cash(): number {
    if (online()) return game()?.store.wallet().cash ?? 0;
    return ctx.services.tryGet('economy')?.balance ?? ctx.state.money;
  }
  const owns = (id: WeaponId): boolean => weapons()?.owned().includes(id) ?? false;
  function room_(id: WeaponId): number {
    const f = WEAPONS[id].firearm;
    if (!f) return 0;
    const w = weapons();
    if (!w) return 0;
    if (!online()) return w.ammoRoom(id);
    // Online the server holds the rounds; this is only for the button (the server checks the room).
    return f.maxReserve - (w.info().ammo[id]?.reserve ?? 0);
  }

  // ------------------------------------------------------------------ the counter's state

  function state(): CounterState {
    const line = lines[index];
    const card = line ? weaponCard(line.weapon) : null;
    const owned = line ? owns(line.weapon) : false;
    const b = line ? cardButtons(line, { owned, room: room_(line.weapon), cash: cash() }) : null;
    return {
      touch: touchUi.enabled,
      cash: `£${cash().toLocaleString('en-GB')}`,
      index,
      items: lines.map((l) => ({ id: l.weapon, owned: owns(l.weapon) })),
      card,
      price: line ? priceText(line) : '',
      owned,
      buy: b?.buy ?? null,
      short: b?.short ?? 0,
      ammo: b?.ammo ?? null,
      why: note || b?.why || '',
      // Red only for a refusal or a shortfall; "rounds once you have the gun" is just how it works.
      whyBad: note ? noteBad : !!b && (b.short > 0 || /short/.test(b.why)),
      quote,
      busy,
    };
  }

  function refresh(): void {
    if (ui?.roomShown) ui.render(state());
  }

  /** Put a weapon in the showcase; `dir` is the way it slides in (+1 from the right, -1 from the left, 0 none). */
  function choose(i: number, dir?: number): void {
    if (!lines.length) return;
    const n = lines.length;
    const next = ((i % n) + n) % n;
    if (next === index) return;
    const d = dir ?? (index < 0 ? 0 : Math.sign(next - index));
    index = next;
    note = '';
    room?.select(index, d);
    ctx.services.tryGet('audio')?.ui('tick');
    quote = SID_LINES.weapon[lines[index]!.weapon] ?? '';
    refresh();
  }

  // ------------------------------------------------------------------ buying

  async function buy(kind: 'weapon' | 'ammo'): Promise<void> {
    const line = lines[index];
    if (!line || busy || !visit.inside) return;
    const item = kind === 'weapon' ? line.item : line.ammo?.item;
    if (!item) return;
    let r: { ok: boolean; reason?: string };
    if (online()) {
      const g = game();
      if (!g) return;
      busy = true;
      refresh();
      const qty = kind === 'weapon' ? 1 : line.ammo!.qty;
      const res = await g.request('supplierBuy', { supplier: ONLINE_DEALER, def: item, qty, to: { k: 'self' } }, { quiet: true });
      busy = false;
      r = res.ok ? { ok: true } : { ok: false, reason: g.actions.explain(res.error!) };
    } else {
      r = weapons()?.buy(item) ?? { ok: false, reason: 'He doesn’t do that.' };
    }
    if (!visit.inside) return;
    const audio = ctx.services.tryGet('audio');
    if (r.ok) {
      // The sale: the till, the stamp, the light flares and the weapon drops out of frame into your coat.
      audio?.ui('cash');
      audio?.ui('stamp');
      noteBad = false;
      note = kind === 'weapon' ? `Bought the ${WEAPONS[line.weapon].name.toLowerCase()}. ${online() ? 'It’s in your pockets.' : 'It’s in your hand.'}` : `Bought a box of ${line.ammo!.qty} ${line.ammo!.name}.`;
      quote = pickLine(kind === 'weapon' ? SID_LINES.sold : SID_LINES.rounds);
      if (kind === 'weapon') {
        ui?.celebrate('In your coat');
        room?.pocket();
      } else ui?.celebrate(`+${line.ammo!.qty} rounds`);
    } else {
      audio?.ui('denied');
      noteBad = true;
      note = r.reason ?? 'No.';
      const why = (r.reason ?? '').toLowerCase();
      quote = pickLine(why.includes('cash') || why.includes('fund') ? SID_LINES.short : why.includes('already') ? SID_LINES.got : why.includes('shooter') || why.includes('need') ? SID_LINES.noGun : why.includes('full') ? SID_LINES.full : SID_LINES.short);
      ui?.celebrate('No sale', true);
    }
    refresh();
  }

  function primary(): void {
    const line = lines[index];
    if (!line) return choose(0);
    if (!owns(line.weapon)) void buy('weapon');
    else if (line.ammo) void buy('ammo');
  }

  // ------------------------------------------------------------------ in and out

  function canEnter(): string | null {
    if (online()) return game()?.active ? null : 'Not now.';
    if ((ctx.services.tryGet('heat')?.level ?? 0) > 0) return `${SID_NAME} won’t open the door with the law after you. Lose them first.`;
    return null;
  }

  function begin(): void {
    const block = canEnter();
    if (block) {
      ctx.services.tryGet('ui')?.toast(block, 'warn');
      ctx.services.tryGet('audio')?.ui('denied');
      return;
    }
    if (!ui) {
      ctx.services.tryGet('ui')?.toast(`${SID_NAME}: "Cash only, son."`);
      return;
    }
    const uiSvc = ctx.services.tryGet('ui');
    if (uiSvc && !uiSvc.openOverlay(ui, 'fence', () => abort())) return;
    if (!visit.enter()) return;
    const sid = contactPerk(ctx, 'sid');
    lines = online() ? onlineLines(sid) : offlineLines(sid);
    index = -1;
    note = '';
    quote = '';
    ui.setOpen(true);
    ui.setFade(0);
    ctx.uiRoot?.classList.add('wpn-room-open');
    ctx.services.tryGet('player')?.controls.acquire('backRoom');
    ctx.input.clearTouch?.();
    ctx.services.tryGet('audio')?.ui('whoosh');
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', swallow, true);
  }

  function leave(): void {
    if (visit.leave()) ctx.services.tryGet('audio')?.ui('back');
  }

  function putRoomUp(): void {
    if (!ui || !ctx.render) return;
    const tier = ctx.services.tryGet('renderPipeline')?.quality ?? 'low';
    room = new BackRoomScene(
      lines.map((l) => l.weapon),
      ctx.render.renderer,
      tier !== 'low',
    );
    sceneOverride.scene = room.scene;
    sceneOverride.camera = room.camera;
    viewKey = '';
    ui.setRoom(true);
    // The first weapon is already on show as the lights come up.
    index = -1;
    choose(0, 0);
    frame();
    refresh();
  }

  function takeRoomDown(): void {
    if (room && sceneOverride.scene === room.scene) {
      sceneOverride.scene = null;
      sceneOverride.camera = null;
    }
    room?.dispose();
    room = null;
    ui?.setRoom(false);
  }

  /** The visit is over: give the screen, the keys and the player back. */
  function finish(closeOverlay: boolean): void {
    takeRoomDown();
    if (closeOverlay && ui) ctx.services.tryGet('ui')?.closeOverlay(ui);
    ui?.setOpen(false);
    ctx.uiRoot?.classList.remove('wpn-room-open');
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('keyup', swallow, true);
    ctx.services.tryGet('player')?.controls.drop('backRoom');
    // Keys released while the room had them never reached the game: let it forget them.
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('blur'));
    lines = [];
    index = -1;
    busy = false;
  }

  /** The UI took the screen back (pause, a result card): out at once, no fade. */
  function abort(): void {
    if (!visit.busy) return;
    visit.reset();
    finish(false);
  }

  /** Out at once and let go of the screen (busted, wasted, paused, in a car). */
  function bail(): void {
    if (!visit.busy) return;
    visit.reset();
    finish(true);
  }

  /** Frame the showcase into the part of the screen the panel and the strip leave (only when the layout changed). */
  function frame(): void {
    if (!room || !ui || !ctx.render) return;
    const c = ctx.render.canvas.getBoundingClientRect();
    const s = ui.stage.getBoundingClientRect();
    const key = `${c.width}|${c.height}|${s.left}|${s.top}|${s.width}|${s.height}`;
    if (key === viewKey) return;
    viewKey = key;
    room.setView(c.width, c.height, { x: s.left - c.left, y: s.top - c.top, w: s.width, h: s.height });
    const r = ui.root.getBoundingClientRect();
    ui.setFocus(s.left - r.left + s.width / 2, s.top - r.top + s.height / 2);
  }

  const swallow = (e: KeyboardEvent): void => {
    e.stopImmediatePropagation();
  };

  /** Keys the UI's menu routing doesn't take (E / B / F leave, digits pick, R buys rounds). */
  function onKey(e: KeyboardEvent): void {
    e.stopImmediatePropagation();
    if (e.repeat || !visit.inside) return;
    const c = e.code;
    if (c === 'Escape' || c === 'Backspace' || c === 'KeyE' || c === 'KeyB' || c === 'KeyF') {
      e.preventDefault();
      leave();
    } else if (c.startsWith('Digit') || c.startsWith('Numpad')) {
      const n = Number(c.replace(/^\D+/, ''));
      if (n >= 1 && n <= lines.length) choose(n - 1);
    } else if (c === 'KeyR') void buy('ammo');
    else if (c === 'ArrowLeft' || c === 'KeyA' || c === 'ArrowUp' || c === 'KeyW') choose(index - 1, -1);
    else if (c === 'ArrowRight' || c === 'KeyD' || c === 'ArrowDown' || c === 'KeyS') choose(index + 1, 1);
    else if (c === 'Enter' || c === 'Space') {
      e.preventDefault();
      primary();
    }
  }

  // ------------------------------------------------------------------ the hut

  function placeHut(): void {
    const f = weapons()?.fence;
    if (!f) return;
    door = { pos: { x: f.pos.x, z: f.pos.z }, heading: f.heading };
    const p = hutPlacement(door.pos);
    const shape = { type: 'box' as const, hx: HUT.width / 2, hz: HUT.depth / 2 };
    // Only where the yard is clear (another layout may have something there): the door works either way.
    if (!ctx.physics.isShapeFree(shape, p.center, p.angle, Layer.STATIC)) return;
    collider = ctx.physics.addStatic({ kind: 'box', center: p.center, halfX: HUT.width / 2, halfZ: HUT.depth / 2, angle: p.angle, tag: 'sid-hut' });
    if (ctx.render) {
      hut = buildHut();
      hut.root.position.set(p.center.x, 0, p.center.z);
      hut.root.rotation.y = p.angle;
      ctx.scene.add(hut.root);
    }
  }

  function info(): BackRoomInfo {
    const s = state();
    return {
      phase: visit.phase,
      open: visit.busy,
      roomUp: !!room,
      items: lines.map((l) => l.weapon),
      selected: lines[index]?.weapon ?? null,
      card: s.card ? { name: s.card.name, price: s.price, lethal: s.card.lethal, bars: s.card.bars.map((b) => ({ key: b.key, value: +b.value.toFixed(3), text: b.text })) } : null,
      buy: s.buy,
      short: s.short,
      ammo: s.ammo,
      why: s.why,
      say: s.quote,
    };
  }

  const service: BackRoomService = {
    get open() {
      return visit.busy;
    },
    get phase() {
      return visit.phase;
    },
  };

  return {
    id: 'backRoom',
    order: 715,
    init(c) {
      ctx = c;
      c.services.provide('backRoom', service);
      c.hooks.register('backRoomState', () => info());
      c.hooks.register('backRoomScreenPos', (weapon) => {
        if (!room || !ui) return null;
        const i = lines.findIndex((l) => l.weapon === weapon);
        return i < 0 ? null : ui.chipCenter(i);
      });
    },
    start() {
      placeHut();
      const interactions = ctx.services.tryGet('interactions');
      if (!door || !interactions) return;
      const d = door;
      if (ctx.uiRoot && typeof document !== 'undefined' && ctx.render) {
        ui = new CounterUi(ctx.uiRoot, {
          prev: () => choose(index < 0 ? lines.length - 1 : index - 1, -1),
          next: () => choose(index + 1, 1),
          choose: (i) => choose(i),
          buy: (k) => void buy(k),
          primary,
          leave,
        });
      }
      offs.push(
        interactions.register({
          id: 'weapons:fence',
          pos: () => d.pos,
          radius: DOOR_REACH,
          priority: 2,
          enabled: () => !visit.busy && (!online() || !!game()?.active),
          prompt: () =>
            (ctx.services.tryGet('heat')?.level ?? 0) > 0 && !online()
              ? `${SID_NAME}'s door: shut while the law is after you`
              : `Go in: ${SID_NAME}, the fence (blades, shooters, rounds)`,
          onInteract: () => begin(),
        }),
      );
      offs.push(
        ctx.events.on('playerWasted', () => bail()),
        ctx.events.on('playerBusted', () => bail()),
      );
    },
    fixedUpdate() {
      if (!visit.inside) return;
      // The pad's interact button walks back out (as it walked in); the keyboard's E comes via onKey.
      if (ctx.input.pressed('enterExit')) leave();
    },
    update(dt) {
      if (!visit.busy) return;
      clock += dt;
      // The law at the door, a car, a pause: straight out.
      const outcome = ctx.services.tryGet('police')?.outcome ?? 'none';
      const p = ctx.services.tryGet('player');
      if (outcome !== 'none' || ctx.state.mode !== 'playing' || (p && p.mode !== 'foot')) {
        bail();
        return;
      }
      if (visit.inside && door && p && Math.hypot(p.pos.x - door.pos.x, p.pos.z - door.pos.z) > DOOR_REACH + 1.5) leave();
      const ev = visit.update(dt);
      if (ev === 'swapIn') putRoomUp();
      else if (ev === 'swapOut') takeRoomDown();
      else if (ev === 'out') {
        finish(true);
        return;
      }
      ui?.setFade(visit.black);
      if (room) {
        frame();
        room.update(dt);
        // The card redraws on changes, and twice a second for the cash.
        if (clock >= nextRefresh) {
          nextRefresh = clock + 0.5;
          refresh();
        }
      }
    },
    dispose() {
      bail();
      for (const off of offs) off();
      offs.length = 0;
      ui?.dispose();
      ui = null;
      hut?.dispose();
      hut = null;
      if (collider) ctx.physics.removeStatic(collider);
      collider = null;
    },
  };
}
